import type { RumErrorEvent } from "@datadog/browser-rum";
import { datadogRum } from "@datadog/browser-rum";
import { reactPlugin } from "@datadog/browser-rum-react";

import { FetchError } from "common/fetch/errors";

import { getErrorTrackingUserData } from "./initialization";

/**
 * Constant indicating whether Datadog RUM should be initialized.
 * Evaluated once at module load time.
 *
 * NOTE: This is only used internally by initDatadogRum(). Consumer code should
 * NOT import this constant - instead, call Datadog APIs directly. They fail
 * silently when RUM is not initialized, which is the intended behavior.
 */
export const USE_DATADOG = !!(
  typeof window !== "undefined" &&
  window.USE_DATADOG_RUM &&
  window.DATADOG_RUM_APPLICATION_ID &&
  window.DATADOG_RUM_CLIENT_TOKEN
);

/**
 * Every one of our bundles is served from `<static host>/static/dist/`, which is also the
 * prefix Datadog resolves source maps against (`minifiedPathPrefix` in vite.config.ts).
 * A stack with no frame under this prefix never executed in our bundle.
 */
const FIRST_PARTY_BUNDLE_PATH = "/static/dist/";

/**
 * Storefronts can run on clinic-owned vanity domains, so a vetcove hostname is not a reliable
 * marker of one of our own requests. Our API is always same-origin, Turbo is the one
 * cross-origin host we call from the browser, and our static assets keep the bundle path even
 * when served from the CDN rather than the app origin.
 */
const isFirstPartyRequest = (url: string | undefined): boolean =>
  !!url &&
  (url.startsWith(window.location.origin) ||
    (!!window.TURBO_HOST && url.startsWith(window.TURBO_HOST)) ||
    url.includes(FIRST_PARTY_BUNDLE_PATH));

/**
 * Whether an error originated in our own application code rather than in a script we do not
 * control — partner GTM tags on clinic storefronts, browser extensions, injected widgets.
 */
const isFirstPartyError = (event: RumErrorEvent): boolean => {
  // addError() is only reachable from our code (captureException, ErrorBoundary)
  if (event.error.source === "custom") {
    return true;
  }

  // Network errors carry no stack, so the request target is the only signal of provenance
  if (event.error.source === "network") {
    return isFirstPartyRequest(event.error.resource?.url);
  }

  // console.error() puts the call site on handling_stack; `stack`, when present at all,
  // describes the logged value rather than whoever logged it
  return [event.error.stack, event.error.handling_stack].some((stack) =>
    stack?.includes(FIRST_PARTY_BUNDLE_PATH),
  );
};

const isNoiseError = (event: RumErrorEvent): boolean => {
  const message = event.error?.message || "";
  const stack = event.error?.stack || "";
  const errorType = event.error?.type || "";

  // Browser extension scripts are outside our control and are not actionable
  if (
    stack.includes("chrome-extension://") ||
    stack.includes("moz-extension://") ||
    stack.includes("safari-extension://") ||
    stack.includes("safari-web-extension://")
  ) {
    return true;
  }

  if (
    stack.includes("googletagmanager.com") ||
    stack.includes("cdn.popt.in") ||
    stack.includes("file://") ||
    stack.includes("@webkit-masked-url")
  ) {
    return true;
  }

  // Google Analytics / gtag throws a bare `Error: ga` from its own minified internals
  // (frames like `ph`/`oh`/`nq` with `undefined` files). On clinic storefronts the gtag
  // snippet is inlined into the page HTML rather than loaded from googletagmanager.com, so
  // the stack points at the vanity domain instead of the CDN and the check above misses it.
  // The literal "ga" message is never something we throw ourselves.
  if (message === "ga") {
    return true;
  }

  if (
    message.includes("Object Not Found Matching Id") ||
    message === "undefined" ||
    // Both wordings of the same benign layout-loop warning: "loop completed with
    // undelivered notifications" (Chrome) and "loop limit exceeded" (Firefox, older Chrome)
    message.includes("ResizeObserver loop")
  ) {
    return true;
  }

  // Bare or minified messages that arrive with no usable stack — Safari and in-app
  // webviews report these with an empty stack or frames whose file is `undefined`,
  // and they are spread evenly across unrelated views rather than pointing at one
  // flow, so there is nothing to act on even when the underlying failure is real.
  if (
    message === "Ba" ||
    message === "No response" ||
    // iOS Safari Web Extension messaging bridge (password managers, coupon/shopping
    // extensions) rejects with a bare "No response received." when the injected content
    // script gets no reply. Unhandled, empty stack, scattered across every view — never
    // phrasing we produce ourselves.
    message === "No response received." ||
    message === "Message Timeout"
  ) {
    return true;
  }

  // Browser storage failures — Safari rejects IndexedDB / Cache writes when the origin
  // is over quota or storage is disabled, which we cannot influence from app code.
  if (message.includes("Failed writing data to the file system")) {
    return true;
  }

  // Browser extension runtime/messaging noise injected into the page
  if (
    message.includes("runtime.sendMessage(). Tab not found") ||
    message.includes("No Listener: tabs:outgoing.message.ready") ||
    // Another extension message-passing "No Listener" variant, observed only on iOS
    // Safari with native-code-only stacks across unrelated views.
    message.includes("No Listener: pcp_refresh") ||
    message.includes("Failed to initialize messaging: tx_attempts_exceeded") ||
    message.includes("_AutofillCallbackHandler") ||
    message.includes("SymBrowser_Modify") ||
    message.includes("__firefox__") ||
    message.includes("fixinatorInputs") ||
    message.includes("WKWebView API client did not respond to this postMessage") ||
    // Coupon/shopping extensions inject a script that reads `this.merchants` off their
    // own config object; observed only on product and rebate pages, never in our bundle.
    message.includes("this.merchants")
  ) {
    return true;
  }

  // DuckDuckGo browser injects a content-scope-script whose messaging layer throws
  // "feature named `<name>` was not found" (e.g. `pageContext`) when it references a
  // privacy/AI feature that isn't registered on the page. It comes from the browser's
  // injected script, not our bundle, and is only observed in DuckDuckGo. We never throw
  // this shape ourselves, so treat any "feature named ... was not found" as noise.
  if (message.includes("feature named") && message.includes("was not found")) {
    return true;
  }

  // Firefox-only "Property 'handleEvent' is not callable." — thrown from inside
  // Firefox's event-dispatch internals (empty stack, unhandled, scattered across
  // every page) when an object registered via addEventListener has a non-callable
  // handleEvent. We register no object-style listeners, so this comes from browser
  // extensions / injected third-party scripts and is not actionable in app code.
  if (message.includes("Property 'handleEvent' is not callable")) {
    return true;
  }

  // Cross-origin frame access from embedded contexts (third-party scripts,
  // extensions, embedded frames) — not actionable in app code.
  //   - Chrome:  "Blocked a frame with origin ..."
  //   - Firefox: "Permission denied to access property \"dispatchEvent\" on cross-origin object"
  if (
    message.includes("Failed to read a named property 'toJSON' from 'Window'") ||
    message.includes("Blocked a frame with origin") ||
    message.includes("cross-origin object")
  ) {
    return true;
  }

  // Ably WebSocket lifecycle events — not actionable app errors.
  //
  // Matched on message alone, with no check that the error actually came from Ably.
  // Ably surfaces these as unhandled promise rejections from inside its own client,
  // and neither way of identifying the origin survives to beforeSend: the rejection
  // value is not an ErrorInfo instance, and the production stack holds only minified
  // bundle URLs. The `node_modules/ably` path exists exclusively on the
  // sourcemap-resolved error.file that Datadog derives server-side, which is not
  // available here. None of these strings are phrasing we ever produce ourselves.
  if (
    message.includes("Channel detached") ||
    message.includes("Connection to server") ||
    message.includes("Connection failed or disconnected by server") ||
    message.includes("Connection clos") ||
    message.includes("mismatched clientId for existing connection") ||
    message.includes("Unable to connect (network unreachable)") ||
    message.includes("Channel attach timed out") ||
    // Covers every channel state Ably rejects operations in (suspended, detaching, failed)
    message.includes("Channel operation failed as channel state is") ||
    message.includes("Presence state is out of sync") ||
    message.includes("Unable to publish message; per-connection publish Rate limit exceeded") ||
    message.includes("Could not find path: /comet/") ||
    message.includes("Client has been destroyed") ||
    // Emitted (Ably error 80019) when a realtime token fetch to our ably-auth endpoint
    // fails — a transient client-side network hiccup that our authCallback already
    // handles gracefully and Ably retries on its own.
    message.includes("Client configured authentication provider request failed") ||
    message.includes("Empty reason")
  ) {
    return true;
  }

  // Cross-origin script errors with zero diagnostic value
  if (message.toLowerCase().includes("script error")) {
    return true;
  }

  // Stale-bundle chunk loads. A client holding an old index.html requests a hashed chunk
  // that no longer exists after a deploy and gets an HTML error page back, which the
  // browser then fails to parse as a module. LoadStatusProvider already handles this at
  // the product level by forcing a reload once a page goes stale, so there is nothing
  // left to fix in app code.
  //
  // "Unexpected token 'else'" comes from in-app webviews (Chrome Mobile WebView on the
  // client app) whose proxies rewrite our JavaScript and truncate it mid-statement.
  if (
    message.includes("Importing a module script failed") ||
    message.includes("Unexpected token '<'") ||
    message.includes("Unexpected token 'else'")
  ) {
    return true;
  }

  // CSP violation reports — addressed at the policy level, not app code
  if (errorType.endsWith("-src") || message.includes("csp_violation")) {
    return true;
  }

  // Mapbox touchmove intervention on mobile — known library issue, not actionable
  if (errorType === "IgnoredEventCancel") {
    return true;
  }

  // Mapbox network/tile load failures — environmental, non-actionable
  if (
    stack.includes("mapbox-gl") &&
    (message.includes("Load failed") ||
      message.includes("Failed to fetch") ||
      message.includes("NetworkError when attempting to fetch") ||
      message.includes("Cannot read properties of undefined (reading 'send')"))
  ) {
    return true;
  }

  // Mapbox tile decode failures — mapbox-gl logs a handled console error when
  // window.createImageBitmap() rejects while decoding a tile blob. Observed almost
  // exclusively on mobile Safari when the tab is backgrounded; non-fatal and outside
  // our control. The raw (minified) stack does not contain "mapbox-gl" in production
  // — that only appears in the sourcemap-resolved error.file, which isn't available
  // here — so we scope this to the full, mapbox-specific wrapper message instead.
  if (
    message.includes("Could not load image because of") &&
    message.includes("Note that SVGs are not supported")
  ) {
    return true;
  }

  // Third-party SDK network failures (Amplitude, Segment, Firebase, etc.)
  if (
    message.includes("Amplitude Logger") ||
    stack.includes("cdn.amplitude.com") ||
    message.includes("[analytics.js] Failed to load Analytics.js") ||
    message.includes("Error sending segment performance metrics") ||
    message.includes("Can't find variable: gmo") ||
    // Firebase Remote Config reports its own fetch failures when the client is offline
    // or the request is blocked; the SDK serves cached/default values and retries.
    message.includes("remoteconfig/fetch-client-network")
  ) {
    return true;
  }

  // Third-party storefront scripts injected by external integrations (non-vetcove bundles)
  if (
    message.includes("Error in discount_upto:") ||
    message.includes("Error in first_time_subscription_price:")
  ) {
    return true;
  }

  // react-query "No QueryClient set" — thrown when our client bundle runs outside its
  // normal provider tree (clinic marketing embeds, the /challenge interstitial, scraped/
  // saved page copies), almost always by bots/crawlers. It is caught by an error boundary
  // and never occurs on real shop flows, so it is not actionable.
  if (message.includes("No QueryClient set")) {
    return true;
  }

  return false;
};

/**
 * Initialize Datadog RUM.
 * Call this during app initialization.
 */
export const initDatadogRum = (): void => {
  if (!USE_DATADOG) {
    return;
  }

  datadogRum.init({
    applicationId: window.DATADOG_RUM_APPLICATION_ID,
    clientToken: window.DATADOG_RUM_CLIENT_TOKEN,
    site: window.DD_SITE,
    service: window.DD_SERVICE,
    env: window.SETTINGS,
    version: RELEASE_VERSION,
    sessionSampleRate: window.DATADOG_RUM_SESSION_SAMPLE_RATE,
    sessionReplaySampleRate: window.DATADOG_RUM_SESSION_REPLAY_SAMPLE_RATE,
    defaultPrivacyLevel: "mask-user-input",
    allowedTracingUrls: [
      {
        match: /^https:\/\/.*\.vetcove\.com\/api/,
        propagatorTypes: ["tracecontext", "datadog"],
      },
    ],
    // React plugin for component tracking
    // NOTE: Currently using router: false because we don't have a standard router implementation
    // TODO: Consider implementing custom router logic to normalize route patterns for better grouping
    // Examples: /orders/123/items/456 -> /orders/:id/items/:id
    // This would improve performance monitoring and error grouping in Datadog
    plugins: [reactPlugin({ router: false })],
    beforeSend: (event, context) => {
      if (event.type === "error") {
        if ("error" in context && context.error instanceof FetchError) {
          return false;
        }
        if (isNoiseError(event)) {
          return false;
        }
        // Third-party code is tagged rather than dropped so it can be excluded or queried
        // from Datadog without a deploy, and so misclassifications stay visible.
        if (!isFirstPartyError(event)) {
          // eslint-disable-next-line no-param-reassign -- mutation is the only way to enrich
          event.context = { ...event.context, provenance: "third_party" };
        }
        return true;
      }

      return true;
    },
  });

  // Set user context for Datadog RUM
  const userData = getErrorTrackingUserData();
  if (userData) {
    datadogRum.setUser({
      id: `${userData.id}`,
      email: userData.email,
    });
  }
};
